import React, { useState } from 'react';
import { Outlet, Navigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
  FiHome,
  FiMapPin,
  FiCalendar,
  FiUsers,
  FiStar,
  FiSettings,
  FiLogOut,
  FiMenu,
  FiX,
  FiGrid
} from 'react-icons/fi';

const AdminLayout = () => {
  const { admin, logoutAdmin } = useAuth();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  if (!admin) {
    return <Navigate to="/admin/login" replace />;
  }

  const menuItems = [
    { path: '/admin', icon: FiHome, label: 'Dashboard', exact: true },
    { path: '/admin/apartments', icon: FiGrid, label: 'Apartments' },
    { path: '/admin/locations', icon: FiMapPin, label: 'Locations' },
    { path: '/admin/bookings', icon: FiCalendar, label: 'Bookings' },
    { path: '/admin/users', icon: FiUsers, label: 'Users' },
    { path: '/admin/reviews', icon: FiStar, label: 'Reviews' },
    { path: '/admin/settings', icon: FiSettings, label: 'Settings' }
  ];

  const isActive = (path, exact) => {
    if (exact) return location.pathname === path;
    return location.pathname.startsWith(path);
  };

  const styles = {
    container: {
      display: 'flex',
      minHeight: '100vh',
      background: '#f5f5f5'
    },
    sidebar: {
      width: '260px',
      background: '#1a1a2e',
      color: 'white',
      position: 'fixed',
      top: 0,
      left: 0,
      bottom: 0,
      zIndex: 1000,
      transition: 'transform 0.3s ease',
      overflowY: 'auto'
    },
    sidebarMobile: {
      transform: sidebarOpen ? 'translateX(0)' : 'translateX(-100%)'
    },
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.5)',
      zIndex: 999,
      display: sidebarOpen ? 'block' : 'none'
    },
    logo: {
      padding: '24px 20px',
      borderBottom: '1px solid rgba(255,255,255,0.1)'
    },
    logoText: {
      fontSize: '1.25rem',
      fontWeight: '700',
      color: '#d4a5a5'
    },
    logoSub: {
      fontSize: '0.75rem',
      color: 'rgba(255,255,255,0.5)',
      marginTop: '4px'
    },
    nav: {
      padding: '20px 0'
    },
    navItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      padding: '12px 20px',
      color: 'rgba(255,255,255,0.7)',
      textDecoration: 'none',
      transition: 'all 0.2s',
      borderLeft: '3px solid transparent'
    },
    navItemActive: {
      background: 'rgba(212,165,165,0.1)',
      color: '#d4a5a5',
      borderLeftColor: '#d4a5a5'
    },
    navIcon: {
      fontSize: '1.125rem'
    },
    adminInfo: {
      padding: '20px',
      borderTop: '1px solid rgba(255,255,255,0.1)',
      marginTop: 'auto'
    },
    adminName: {
      fontWeight: '600',
      marginBottom: '4px'
    },
    adminEmail: {
      fontSize: '0.75rem',
      color: 'rgba(255,255,255,0.5)'
    },
    logoutBtn: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      marginTop: '16px',
      padding: '10px 16px',
      background: 'rgba(255,255,255,0.1)',
      border: 'none',
      borderRadius: '8px',
      color: 'white',
      cursor: 'pointer',
      width: '100%',
      fontSize: '0.875rem'
    },
    main: {
      flex: 1,
      marginLeft: '260px',
      minHeight: '100vh'
    },
    topBar: {
      background: 'white',
      padding: '16px 24px',
      borderBottom: '1px solid #eee',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between'
    },
    menuBtn: {
      display: 'none',
      padding: '8px',
      background: 'none',
      border: 'none',
      fontSize: '1.5rem',
      cursor: 'pointer'
    },
    breadcrumb: {
      color: '#555',
      fontSize: '0.875rem'
    },
    content: {
      padding: '24px'
    }
  };

  const getPageTitle = () => {
    const path = location.pathname;
    if (path === '/admin') return 'Dashboard';
    const item = menuItems.find(m => path.startsWith(m.path) && m.path !== '/admin');
    return item?.label || 'Admin';
  };

  return (
    <div style={styles.container}>
      {/* Mobile Overlay */}
      <div style={styles.overlay} onClick={() => setSidebarOpen(false)} />

      {/* Sidebar */}
      <aside
        style={{
          ...styles.sidebar,
          ...(window.innerWidth <= 1024 ? styles.sidebarMobile : {})
        }}
        className="admin-sidebar"
      >
        <div style={styles.logo}>
          <div style={styles.logoText}>Kasbia Stays</div>
          <div style={styles.logoSub}>Admin Panel</div>
        </div>

        <nav style={styles.nav}>
          {menuItems.map(item => (
            <Link
              key={item.path}
              to={item.path}
              style={{
                ...styles.navItem,
                ...(isActive(item.path, item.exact) ? styles.navItemActive : {})
              }}
              onClick={() => setSidebarOpen(false)}
            >
              <item.icon style={styles.navIcon} />
              {item.label}
            </Link>
          ))}
        </nav>

        <div style={styles.adminInfo}>
          <div style={styles.adminName}>{admin.name}</div>
          <div style={styles.adminEmail}>{admin.email}</div>
          <button style={styles.logoutBtn} onClick={logoutAdmin}>
            <FiLogOut /> Logout
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main style={styles.main} className="admin-main">
        <div style={styles.topBar}>
          <button
            style={styles.menuBtn}
            className="admin-menu-btn"
            onClick={() => setSidebarOpen(!sidebarOpen)}
          >
            {sidebarOpen ? <FiX /> : <FiMenu />}
          </button>
          <div style={styles.breadcrumb}>
            Admin / {getPageTitle()}
          </div>
          <Link to="/" style={{ color: '#1a3a5c', textDecoration: 'none', fontSize: '0.875rem' }}>
            View Site
          </Link>
        </div>

        <div style={styles.content}>
          <Outlet />
        </div>
      </main>

      <style>{`
        @media (max-width: 1024px) {
          .admin-sidebar {
            transform: ${sidebarOpen ? 'translateX(0)' : 'translateX(-100%)'} !important;
          }
          .admin-main {
            margin-left: 0 !important;
          }
          .admin-menu-btn {
            display: block !important;
          }
        }
      `}</style>
    </div>
  );
};

export default AdminLayout;
